import { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Users, Target, CheckCircle, XCircle, 
  Pause, Play, Archive, Trash2, RefreshCw, Plus, FlaskConical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { api } from '@/utils/apiClient';
import { toast } from 'sonner';

export default function ABTestingDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTestForm, setNewTestForm] = useState({
    name: '',
    discountA: 20,
    discountB: 30
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/ab-testing/dashboard/summary');
      setDashboard(res.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load A/B testing dashboard');
      setLoading(false);
    }
  };

  const fetchTestChartData = async (testId) => {
    try {
      const res = await api.get(`/ab-testing/dashboard/test/${testId}/chart-data`, { params: { granularity: 'day' } });
      setChartData(res.data);
    } catch (err) {
      toast.error('Failed to load chart data');
    }
  };

  const updateTestStatus = async (testId, status) => {
    try {
      await api.put(`/ab-testing/tests/${testId}/status`, null, { params: { status } });
      toast.success(`Test ${status === 'paused' ? 'paused' : status === 'completed' ? 'completed' : 'resumed'}`);
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to update test status');
    }
  };

  const archiveTest = async (testId) => {
    try {
      await api.post(`/ab-testing/tests/${testId}/archive`);
      toast.success('Test archived');
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to archive test');
    }
  };

  const deleteTest = async (testId) => {
    if (!confirm('Are you sure you want to delete this test?')) return;
    try {
      await api.delete(`/ab-testing/tests/${testId}`, { params: { delete_data: true } });
      toast.success('Test deleted');
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to delete test');
    }
  };

  const createDiscountTest = async () => {
    try {
      const res = await api.post('/ab-testing/create-discount-test', null, {
        params: { name: newTestForm.name, discount_a: newTestForm.discountA, discount_b: newTestForm.discountB }
      });
      if (res.data.success) {
        toast.success('A/B test created!');
        setShowCreateModal(false);
        setNewTestForm({ name: '', discountA: 20, discountB: 30 });
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Failed to create test');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ab-testing-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">A/B Testing</h2>
          <p className="text-muted-foreground">Optimize discounts and campaigns with data-driven testing</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            New Test
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card p-4 rounded-xl border border-border">
          <div className="flex items-center gap-2 mb-2">
            <FlaskConical className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-muted-foreground">Running Tests</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {dashboard?.summary?.tests_by_status?.running || 0}
          </p>
        </div>
        <div className="bg-card p-4 rounded-xl border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-green-500" />
            <span className="text-sm text-muted-foreground">Total Participants</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {dashboard?.summary?.total_assignments || 0}
          </p>
        </div>
        <div className="bg-card p-4 rounded-xl border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-muted-foreground">Conversions (30d)</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {dashboard?.summary?.conversions_last_30_days || 0}
          </p>
        </div>
        <div className="bg-card p-4 rounded-xl border border-border">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <span className="text-sm text-muted-foreground">Tests with Winner</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {dashboard?.summary?.tests_with_clear_winner || 0}
          </p>
        </div>
      </div>

      {/* Running Tests */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Active Tests</h3>
        
        {dashboard?.running_tests?.length > 0 ? (
          <div className="space-y-4">
            {dashboard.running_tests.map(({ test, results }) => (
              <div 
                key={test.id}
                className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-medium text-foreground">{test.name}</h4>
                    <p className="text-sm text-muted-foreground">{test.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedTest(test);
                        fetchTestChartData(test.id);
                      }}
                    >
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => updateTestStatus(test.id, 'paused')}
                    >
                      <Pause className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => updateTestStatus(test.id, 'completed')}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Variants Comparison */}
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(results?.variants || {}).map(([variantId, data]) => (
                    <div 
                      key={variantId}
                      className={`p-3 rounded-lg ${
                        results?.winner === variantId 
                          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                          : 'bg-muted/50'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-foreground">
                          Variant {variantId}
                          {data.variant?.discount_percent && ` (${data.variant.discount_percent}% off)`}
                        </span>
                        {results?.winner === variantId && (
                          <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                            Winner
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Samples</p>
                          <p className="font-medium text-foreground">{data.total_samples}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Conversions</p>
                          <p className="font-medium text-foreground">{data.conversions}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Conv. Rate</p>
                          <p className="font-medium text-foreground">{data.conversion_rate}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Avg Value</p>
                          <p className="font-medium text-foreground">{data.avg_value.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Statistical Significance */}
                {results?.comparison && (
                  <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {results.is_significant ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-yellow-500" />
                        )}
                        <span className="text-sm text-foreground">
                          {results.is_significant ? 'Statistically Significant' : 'Not Yet Significant'}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        p-value: {results.comparison.p_value.toFixed(4)} | 
                        Improvement: {results.comparison.relative_improvement > 0 ? '+' : ''}
                        {results.comparison.relative_improvement.toFixed(1)}%
                      </div>
                    </div>
                    <Progress 
                      value={Math.min(100, (results.variants?.[Object.keys(results.variants)[0]]?.total_samples || 0) / (test.min_sample_size || 100) * 100)} 
                      className="h-2 mt-2" 
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Progress to min sample size ({test.min_sample_size || 100} per variant)
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FlaskConical className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No active tests</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setShowCreateModal(true)}
            >
              Create Your First Test
            </Button>
          </div>
        )}
      </div>

      {/* Recent Completed Tests */}
      {dashboard?.recent_completed?.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Recent Completed Tests</h3>
          <div className="space-y-2">
            {dashboard.recent_completed.map((test) => (
              <div 
                key={test.id}
                className="flex justify-between items-center p-3 bg-muted/30 rounded-lg"
              >
                <div>
                  <span className="font-medium text-foreground">{test.name}</span>
                  {test.final_results?.winner && (
                    <span className="ml-2 text-sm text-green-600 dark:text-green-400">
                      Winner: Variant {test.final_results.winner}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => archiveTest(test.id)}
                  >
                    <Archive className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => deleteTest(test.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Test Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl p-6 w-full max-w-md border border-border shadow-xl">
            <h3 className="text-lg font-semibold text-foreground mb-4">Create Discount A/B Test</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground">Test Name</label>
                <input
                  type="text"
                  value={newTestForm.name}
                  onChange={(e) => setNewTestForm({...newTestForm, name: e.target.value})}
                  placeholder="e.g., Win-Back Discount Test"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground">Variant A (%)</label>
                  <input
                    type="number"
                    value={newTestForm.discountA}
                    onChange={(e) => setNewTestForm({...newTestForm, discountA: parseInt(e.target.value)})}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground">Variant B (%)</label>
                  <input
                    type="number"
                    value={newTestForm.discountB}
                    onChange={(e) => setNewTestForm({...newTestForm, discountB: parseInt(e.target.value)})}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={createDiscountTest} disabled={!newTestForm.name}>
                Create Test
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
